/**
 * What a person sees when GET /api/me refuses, or when a sign-in came back refused.
 *
 * There is no sign-in form here and there never will be. `docs/identity.md`, revised 2026-09-04:
 * named sign-in is Microsoft Entra directly, and the Worker itself runs the flow at `/api/auth`.
 * Microsoft decides whether this person exists and whether the owners assigned her to this
 * application; the Worker turns the answer into a session cookie; and by the time this screen
 * renders the question of who somebody is has already been asked. Everything this screen can do
 * is explain WHICH refusal happened, in the words the server used, and say what the person can
 * actually do about it.
 *
 * The refusals need different actions, and collapsing them into one "access denied" makes most of
 * them unfixable: no session is fixed by signing in; an unenrolled account is fixed by an owner
 * adding a row, or by signing in as the right account; a revoked session by signing in again after
 * somebody decided it should end; a refusal from Microsoft by an owner assigning the account in the
 * directory. A cleaner reading this at a locked door gets a sentence and a button, never JSON.
 */
import type { ApiError } from '../api.ts';

/** Where the Worker starts the Entra flow. Plain links, because these are full-page redirects. */
const SIGN_IN = '/api/auth/start';
/** The same, asking Microsoft for its account picker, for the person who used the wrong account. */
const SWITCH_ACCOUNT = '/api/auth/start?switch=1';
/** The same, asking Microsoft to take credentials again. */
const SIGN_IN_AGAIN = '/api/auth/start?fresh=1';

/**
 * A sign-in link that comes back to `returnPath`, the address the person was on, query and all. The
 * Worker keeps any path on this origin it is handed (`safeReturnPath`, `api/src/routes/auth.ts`) and
 * goes to the front page without one, which is where a tab the call wall opened at
 * `/find?number=...` used to land once its person had signed in, the number lost (ops #84).
 */
export function withReturn(href: string, returnPath: string): string {
  if (returnPath === '' || returnPath === '/') return href;
  const joiner = href.includes('?') ? '&' : '?';
  return `${href}${joiner}return=${encodeURIComponent(returnPath)}`;
}

interface Guidance {
  readonly title: string;
  readonly next: string;
  /** The one link that fixes it, if a link can. */
  readonly link: { href: string; label: string } | null;
  readonly canRetry: boolean;
}

export function guidanceFor(code: string): Guidance {
  switch (code) {
    case 'not_signed_in':
      return {
        title: 'Not signed in',
        next: 'Owners and the office sign in with their Microsoft work account. Cleaners are invited as guests and sign in with the one-time code Microsoft emails them. There is nothing to install.',
        link: { href: SIGN_IN, label: 'Sign in' },
        canRetry: true,
      };
    case 'not_enrolled':
      return {
        title: 'No account here yet',
        next: 'An owner adds people to this system by hand. There is no way to sign yourself up, which is deliberate: three to seven people ever use this. If you have another account, try that one.',
        link: { href: SWITCH_ACCOUNT, label: 'Sign in as somebody else' },
        canRetry: false,
      };
    case 'session_revoked':
      return {
        title: 'This session was ended',
        next: 'Somebody ended this session on purpose. Sign in again, and if it stops working, ring the office.',
        link: { href: SIGN_IN_AGAIN, label: 'Sign in again' },
        canRetry: false,
      };
    case 'session_expired':
      return {
        title: 'Time to sign in again',
        next: 'A sign-in lasts thirty days, and this one has run out. Sign in again to carry on.',
        link: { href: SIGN_IN, label: 'Sign in' },
        canRetry: false,
      };
    case 'sign_in_refused':
      return {
        title: 'Microsoft would not sign you in',
        next: 'The reason Microsoft gave is above, word for word. Usually the account is not assigned to this application yet, and an owner assigns it in the directory. If you have another account, try that one.',
        link: { href: SWITCH_ACCOUNT, label: 'Sign in as somebody else' },
        canRetry: false,
      };
    case 'sign_in_failed':
      return {
        title: 'That sign-in did not go through',
        next: 'It came back from Microsoft but could not be completed. Try once more; if it happens again, tell Alex what this page says.',
        link: { href: SIGN_IN, label: 'Sign in' },
        canRetry: false,
      };
    case 'bad_state':
      return {
        title: 'That sign-in did not start here',
        next: 'A sign-in begins from this page and nowhere else, so a link somebody sent you cannot sign you in. Start again from here.',
        link: { href: SIGN_IN, label: 'Sign in' },
        canRetry: false,
      };
    case 'sign_in_not_configured':
      return {
        title: 'Sign-in is not set up here yet',
        next: 'This deployment has no sign-in configured. That is a setup step for the owners, not something to fix from here.',
        link: null,
        canRetry: false,
      };
    case 'internal':
      return {
        title: 'The system could not answer',
        next: 'Something went wrong at our end and it has been written down. Signing in again is worth trying first: it writes a fresh session over whatever this browser is holding, and a browser holding something the system cannot read is the usual reason for this. If it happens again, tell Alex what this page says.',
        link: { href: SIGN_IN, label: 'Sign in' },
        canRetry: true,
      };
    case 'no_answer':
      return {
        title: 'No answer from the network',
        next: 'This is almost always signal rather than the system. Wait until a bar comes back and try again.',
        link: null,
        canRetry: true,
      };
    default:
      return {
        title: 'That did not work',
        next: 'If it keeps happening, tell Alex what this page said, word for word. It is written down for exactly that.',
        link: null,
        canRetry: true,
      };
  }
}

export function SignedOut({
  error,
  onRetry,
  returnPath = '/',
}: {
  error: ApiError;
  onRetry: () => void;
  /** Where the sign-in comes back to: the page this screen stands in for, query and all. */
  returnPath?: string;
}) {
  const guidance = guidanceFor(error.code);
  const primary =
    'inline-flex min-h-12 w-full items-center justify-center rounded-lg border border-gold bg-gold px-5 text-base font-medium text-navy';
  const quiet =
    'inline-flex min-h-12 w-full items-center justify-center rounded-lg border border-cream/40 bg-transparent px-5 text-base font-medium text-cream';

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-navy px-5 py-10 text-cream">
      <div className="w-full max-w-md">
        <img
          src="/logo-compact-reverse.svg"
          alt="Faithfully Kept"
          width={900}
          height={593}
          className="mx-auto h-auto w-56"
        />
        <p className="mt-2 text-center text-xs tracking-wide text-cream/60">Operations</p>

        <div className="mt-8 rounded-xl border border-cream/20 bg-cream/5 p-5">
          <h1 className="font-serif text-xl">{guidance.title}</h1>
          {/* The server's own sentence, kept whole. It is the most carefully written text here. */}
          <p className="mt-2 text-sm text-cream/90">{error.message}</p>
          <p className="mt-3 text-sm text-cream/70">{guidance.next}</p>

          {guidance.link ? (
            <a href={withReturn(guidance.link.href, returnPath)} className={`mt-5 ${primary}`}>
              {guidance.link.label}
            </a>
          ) : null}

          {guidance.canRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className={`${guidance.link ? 'mt-3' : 'mt-5'} ${guidance.link ? quiet : primary}`}
            >
              Try again
            </button>
          ) : null}
        </div>

        <p className="mt-6 text-xs text-cream/50">
          Clients never sign in here. They get a website, a phone call, an invoice and a message.
        </p>
      </div>
    </div>
  );
}
